import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { I as Ie } from './index.es.js';
import { s as sanitizeHtml } from './index3.js';
import { c as checkRequiredProp } from './components.utils.js';

const mgBsIdentityCardCss = "@font-face{font-family:\"Open Sans\";src:url(\"fonts/OpenSans-Regular.ttf\") format(\"truetype\")}@font-face{font-family:\"Open Sans\";font-weight:600;src:url(\"fonts/OpenSans-SemiBold.ttf\") format(\"truetype\")}@font-face{font-family:\"Open Sans\";font-weight:700;src:url(\"fonts/OpenSans-Bold.ttf\") format(\"truetype\")}p{margin:0}p+p{margin-top:var(--mg-b-size-8)}h1,.mg-u-h1{font-family:var(--mg-b-font-family-heading);font-weight:bold}h2,.mg-u-h2{font-family:var(--mg-b-font-family-heading);font-weight:bold}h3,.mg-u-h3{font-family:var(--mg-b-font-family-heading);font-weight:bold}h4,.mg-u-h4{font-family:var(--mg-b-font-family-heading);font-weight:bold}h5,.mg-u-h5{font-family:var(--mg-b-font-family-heading);font-weight:bold}h6,.mg-u-h6{font-family:var(--mg-b-font-family-heading);font-weight:bold}h1,.mg-u-h1{font-size:var(--mg-b-font-size-h1)}h2,.mg-u-h2{font-size:var(--mg-b-font-size-h2)}h3,.mg-u-h3{font-size:var(--mg-b-font-size-h3)}h4,.mg-u-h4{font-size:var(--mg-b-font-size-h4)}h5,.mg-u-h5{font-size:var(--mg-b-font-size-h5)}h6,.mg-u-h6{font-size:var(--mg-b-font-size-h6)}a{color:var(--mg-b-color-primary);text-decoration:underline}a:hover,a:focus,a:active{text-decoration:none}small{font-size:var(--mg-b-small-font-size)}:host{display:block}.mg-bs-identity-card{--mg-c-card-spacing:1.2rem;--mg-c-card-box-shadow:none}.mg-bs-identity-card__header{display:flex;align-items:start;justify-content:space-between;gap:0.8rem}.mg-bs-identity-card__header-left{display:flex;align-items:center;gap:0.8rem}.mg-bs-identity-card__header-title{display:flex;align-items:center;gap:0.4rem 0.8rem;flex-wrap:wrap}.mg-bs-identity-card__logo{max-width:3.2rem;max-height:3.2rem}.mg-bs-identity-card__identity-name{font-weight:600;size:1.4rem}.mg-bs-identity-card__button{width:max-content}.mg-bs-identity-card__button--mobile{display:none}@media screen and (max-width: 576px){.mg-bs-identity-card__button{width:auto}.mg-bs-identity-card__button--desktop{display:none}.mg-bs-identity-card__button--mobile{display:initial;flex:0 0 100%}}@media screen and (max-width: 576px){.mg-bs-identity-card__tooltip{width:100%}}.mg-bs-identity-card__content{--mg-c-card-spacing:1.2rem;display:flex;flex-direction:column;align-items:flex-start;gap:1.6rem;padding:var(--mg-c-card-spacing);margin-top:0.8rem}.mg-bs-identity-card__content--empty{padding:0;margin-top:0}.mg-bs-identity-card__values-group{margin:0;padding:0;display:flex;flex-direction:column;align-items:flex-start;gap:0.4rem}.mg-bs-identity-card__values-entry{display:flex;align-items:flex-start;align-content:flex-start;column-gap:0.8rem;flex-wrap:wrap}.mg-bs-identity-card__values-value{font-weight:600}";
const MgBsIdentityCardStyle0 = mgBsIdentityCardCss;

/**
 * type Logo validation function
 *
 * @param object - Logo
 * @returns Logo type is valid
 */
const isLogo = (object) => {
    const logo = object;
    return typeof logo === 'object' && typeof logo.src === 'string';
};
/**
 * type LogActiono validation function
 *
 * @param object - Action
 * @returns Action type is valid
 */
const isAction = (object) => {
    const action = object;
    let isValid = typeof action === 'object' &&
        'id' in action &&
        typeof action.id === 'string' &&
        'icon' in action &&
        typeof action.icon === 'string' &&
        'label' in action &&
        typeof action.label === 'string';
    if (isValid && 'disabled' in action)
        isValid = typeof action.disabled === 'boolean';
    return isValid;
};
/**
 * type Tag validation function
 *
 * @param object - Tag
 * @returns Tag type is valid
 */
const isTag = (object) => {
    const tag = object;
    let isValid = typeof tag === 'object' && 'content' in tag && typeof tag.content === 'string';
    if (isValid && 'icon' in tag)
        isValid = typeof tag.icon === 'string';
    if (isValid && 'variant' in tag)
        isValid = typeof tag.variant === 'string';
    return isValid;
};
const MgBsIdentityCard$1 = /*@__PURE__*/ proxyCustomElement(class MgBsIdentityCard extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.actionClick = createEvent(this, "action-click", 7);
        this.componentName = 'mg-bs-identity-card';
        /**
         * Action Click Handler
         *
         * @param id - Action ID
         */
        this.actionClickHandler = (id) => {
            this.actionClick.emit(id);
        };
        this.identifier = undefined;
        this.identityName = undefined;
        this.icon = undefined;
        this.logo = undefined;
        this.tags = undefined;
        this.actions = undefined;
        this.values = undefined;
        this.classCollection = new Ie(['mg-bs-identity-card']);
        this.isContentFilled = undefined;
    }
    /**
     * Watch identifier
     */
    validateIdentifier(newValue) {
        checkRequiredProp(this.componentName, 'identifier', newValue);
    }
    /**
     * Watch identityName
     */
    validateIdentityName(newValue) {
        checkRequiredProp(this.componentName, 'identityName', newValue);
    }
    /**
     * Validate if there is an icon or a logo (and not both)
     */
    validateIconOrLogo(newValue, _oldValue, _property) {
        let icon = this.icon;
        let logo = this.logo;
        if (_property === 'icon') {
            icon = newValue;
        }
        else if (_property === 'logo') {
            logo = newValue;
        }
        if (Boolean(icon) && Boolean(logo)) {
            throw new Error(`<${this.componentName}> prop "icon" is incompatible with prop "logo"`);
        }
        if (!Boolean(icon) && !Boolean(logo)) {
            throw new Error(`<${this.componentName}> prop "icon" or prop "logo" must be defined`);
        }
        if (Boolean(logo) && !isLogo(logo)) {
            throw new Error(`<${this.componentName}> prop "logo" must be a Logo`);
        }
    }
    /**
     * Watch tags
     */
    validateTags(newValue) {
        if (Boolean(newValue) && (!Array.isArray(newValue) || newValue.some(tag => !isTag(tag)))) {
            throw new Error(`<${this.componentName}> prop "tags" must be an array of Tag`);
        }
    }
    /**
     * Watch actions
     */
    validateActions(newValue) {
        if (Boolean(newValue)) {
            if (!Array.isArray(newValue)) {
                throw new Error(`<${this.componentName}> prop "actions" must be an Array of Action elements`);
            }
            if (newValue.some(action => !isAction(action))) {
                throw new Error(`<${this.componentName}> prop "actions": each action must be of type "Action"`);
            }
            if (newValue.map(e => e.id).some((element, index) => newValue.map(e => e.id).indexOf(element) !== index)) {
                throw new Error(`<${this.componentName}> prop "actions": the list must not contain similar ids`);
            }
        }
    }
    /**
     * Watch values
     */
    validateValues(newValue) {
        if ((newValue === null || newValue === void 0 ? void 0 : newValue.length) > 0 && newValue.some(group => group.some(entry => !Boolean(entry.value)))) {
            throw new Error(`<${this.componentName}> prop "values": each value must contain a value property`);
        }
    }
    /**
     * Set to true if content is filled
     */
    setIsContentFilled() {
        var _a, _b;
        const isValuesFilled = (_a = this.values) === null || _a === void 0 ? void 0 : _a.some(group => group.length > 0);
        const slotContent = this.element.querySelector('[slot="content"]');
        this.isContentFilled = isValuesFilled || ((_b = slotContent === null || slotContent === void 0 ? void 0 : slotContent.children) === null || _b === void 0 ? void 0 : _b.length) > 0;
    }
    /**
     * Check if props are well configured on init
     */
    componentWillLoad() {
        this.validateIdentifier(this.identifier);
        this.validateIdentityName(this.identityName);
        this.validateIconOrLogo(this.icon, null, 'icon');
        this.validateIconOrLogo(this.logo, null, 'logo');
        this.validateTags(this.tags);
        this.validateActions(this.actions);
        this.validateValues(this.values);
        this.setIsContentFilled();
    }
    /**
     * Render action buttons
     *
     * @param actions - List of action buttons
     * @returns rendered action buttons
     */
    renderActionButtons(actions) {
        const renderDesktopButton = (action) => (h("div", { class: "mg-bs-identity-card__button" }, h("mg-button", { id: `${this.identifier}-${action.id}`, disabled: action.disabled, variant: "flat", label: action.label, "is-icon": true, onClick: () => this.actionClickHandler(action.id) }, h("mg-icon", { icon: action.icon }))));
        return (h("div", { class: "mg-group-elements mg-group-elements--align-right" }, actions.map(action => (h("mg-tooltip", { class: { 'mg-bs-identity-card__tooltip': true, 'mg-bs-identity-card__button--desktop': actions.length > 1 }, message: action.label, key: action.id }, renderDesktopButton(action)))), actions.length > 1 && (h("mg-action-more", { class: "mg-bs-identity-card__button--mobile", items: this.actions.map(action => (Object.assign(Object.assign({}, action), { status: action.disabled && 'disabled', mouseEventHandler: () => this.actionClickHandler(action.id) }))), icon: { icon: 'ellipsis-vertical' } }))));
    }
    /**
     * Render values list
     *
     * @param values - values groups to display
     * @returns HTML Element
     */
    renderValues(values) {
        return values === null || values === void 0 ? void 0 : values.map((group, index) => (h("ul", { class: "mg-bs-identity-card__values-group", key: index }, group.map(entry => {
            return (h("li", { class: "mg-bs-identity-card__values-entry", key: entry.value }, this.renderLabel(entry), " ", this.renderValue(entry)));
        }))));
    }
    /**
     * Render a label
     *
     * @param entry - entry to display label
     * @returns HTML Element
     */
    renderLabel(entry) {
        if ('label' in entry) {
            return h("span", null, entry.label);
        }
        if ('icon' in entry) {
            return h("mg-icon", { size: "large", icon: entry.icon });
        }
    }
    /**
     * Render a single value
     *
     * @param entry - entry to display value
     * @returns HTML Element
     */
    renderValue(entry) {
        if (entry.isTag) {
            return h("mg-tag", { outline: true }, entry.value);
        }
        return Boolean(entry.link) ? (h("span", { class: { 'mg-bs-identity-card__values-value': Boolean(entry.label) || Boolean(entry.icon) } }, h("a", { href: entry.link }, h("span", { innerHTML: sanitizeHtml(entry.value) })))) : (h("span", { class: { 'mg-bs-identity-card__values-value': Boolean(entry.label) || Boolean(entry.icon) }, innerHTML: sanitizeHtml(entry.value) }));
    }
    /**
     * Render
     *
     * @returns HTML Element
     */
    render() {
        var _a, _b;
        return (h("mg-card", { key: '763c235f6b8c5c0e51eff543eeb42c8de9b762e2', class: this.classCollection.join() }, h("article", { key: '135c5b25b867abf7a0bc51c5eaf17f5d39a2eac9', class: "mg-bs-identity-card__card" }, h("header", { key: 'ec7ff9f5a0831cedd36374a3f6722a3ceba23d91', class: "mg-bs-identity-card__header" }, h("div", { key: '5ff63c9be39b86a43c83c0deedac5671a375b9f7', class: "mg-bs-identity-card__header-left" }, this.icon && h("mg-icon", Object.assign({ key: '71055ac38c2a6c521dc64e6b334f1ecfc547542c', variant: "app", variantStyle: "full" }, this.icon)), this.logo && h("img", Object.assign({ key: 'e41399fc941367191da99cca81e671ea3dd47bd5', class: "mg-bs-identity-card__logo" }, this.logo, { "aria-hidden": "true", alt: "" })), h("div", { key: '9c63960647f005b6cbec186222165ad0ecd0f77f', class: "mg-bs-identity-card__header-title" }, h("span", { key: '7b0758cfc2ce6260645d31728e032d660be0959d', class: "mg-bs-identity-card__identity-name" }, this.identityName), (_a = this.tags) === null || _a === void 0 ? void 0 :
            _a.map(tag => (h("mg-tag", Object.assign({}, tag), tag.icon && h("mg-icon", { icon: tag.icon }), " ", tag.content))))), ((_b = this.actions) === null || _b === void 0 ? void 0 : _b.length) > 0 && this.renderActionButtons(this.actions)), h("section", { key: '2c479099f89889f3628c6c4dfa3c4532e7994579', class: { 'mg-bs-identity-card__content': true, 'mg-bs-identity-card__content--empty': !this.isContentFilled } }, this.renderValues(this.values), h("slot", { key: 'aafcaea8ee0ba2dd6c6b3a8b8383f2c7125bbfa3', name: "content" })))));
    }
    get element() { return this; }
    static get watchers() { return {
        "identifier": ["validateIdentifier"],
        "identityName": ["validateIdentityName"],
        "icon": ["validateIconOrLogo"],
        "logo": ["validateIconOrLogo"],
        "tags": ["validateTags"],
        "actions": ["validateActions"],
        "values": ["validateValues"]
    }; }
    static get style() { return MgBsIdentityCardStyle0; }
}, [1, "mg-bs-identity-card", {
        "identifier": [1],
        "identityName": [1, "identity-name"],
        "icon": [16],
        "logo": [16],
        "tags": [16],
        "actions": [16],
        "values": [16],
        "classCollection": [32],
        "isContentFilled": [32]
    }, undefined, {
        "identifier": ["validateIdentifier"],
        "identityName": ["validateIdentityName"],
        "icon": ["validateIconOrLogo"],
        "logo": ["validateIconOrLogo"],
        "tags": ["validateTags"],
        "actions": ["validateActions"],
        "values": ["validateValues"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["mg-bs-identity-card"];
    components.forEach(tagName => { switch (tagName) {
        case "mg-bs-identity-card":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, MgBsIdentityCard$1);
            }
            break;
    } });
}
defineCustomElement$1();

const MgBsIdentityCard = MgBsIdentityCard$1;
const defineCustomElement = defineCustomElement$1;

export { MgBsIdentityCard, defineCustomElement };

//# sourceMappingURL=mg-bs-identity-card.js.map